<template>
	<div
		class="tab"
		:class="[
			{
				'is-disabled': isDisabled,
				'is-mobile': isMobileWebApp,
				'is-change-disabled': isChangeDisabled,
				'-simple': modifiers.includes('simple'),
			},
		]"
	>
		<div
			v-if="isDisabled"
			class="tab__overlay"
		/>
		<div class="tab__wrapper">
			<div
				v-if="!hideHeader"
				class="tab__header"
				:class="{
					'is-tagging-tool': isTaggingToolMode || isMomentsMode,
					'-sticky': stickyHeader,
				}"
			>
				<slot
					name="tabs-menu"
					:selected="selectedKey"
					:select-tab="selectTab"
				>
					<ul
						class="tab__menu"
						data-qa="tabs"
					>
						<li
							v-for="tab in tabs"
							:key="`tab-${tab.key}`"
							class="tab__menu-item"
							:class="{
								'is-tagging-tool': isTaggingToolMode || isMomentsMode,
								'is-active': tab.key === selectedKey,
							}"
							@click="!isDisabled && selectTab(tab.key)"
						>
							<span
								v-if="tab.icon"
								class="tab__menu-icon"
							>
								<Icon
									:icon-name="tab.icon"
									:size="iconSize"
								/>
							</span>
							<div
								v-if="tab.label"
								class="tab__menu-label"
							>
								{{ tab.label }}
							</div>
						</li>
					</ul>
				</slot>
			</div>
			<slot name="above-tab-content" />
			<div
				ref="tabContent"
				class="tab__content"
			>
				<slot :name="`tab-details-${selectedKey}`" />
			</div>
		</div>
		<div
			v-if="isDisabled"
			class="tab__overlay"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '@components/Icon';

export default {
	name:       'Tabs',
	components: { Icon },
	props:      {
		tabs: {
			type:    Array,
			default: () => [],
		},
		isDisabled: {
			type: Boolean,
		},
		isChangeDisabled: {
			type: Boolean,
		},
		iconSize: {
			type:    String,
			default: 'lg',
		},
		activeTab: {
			default: null,
		},
		hideHeader: {
			type:    Boolean,
			default: false,
		},
		stickyHeader: {
			type:    Boolean,
			default: false,
		},
		modifiers: {
			type:      Array,
			default:   () => [],
			validator: (value) => value.every(item => ['simple'].includes(item)), // simple -> used in arena
		},
	},
	data() {
		return {
			selectedKey: null,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			isTaggingToolMode: 'app/isTaggingToolMode',
			isMomentsMode:     'app/isMomentsMode',
		}),
	},
	watch: {
		tabs(newTabs) {
			// If the selected tab doesn't exist in the new list of tabs redirect to first
			const alreadySelectedTab = newTabs.find(tab => tab.key === this.selectedKey);
			if (!alreadySelectedTab) this.updateTab(newTabs[0]?.key);
		},
		activeTab(value) {
			if (value !== this.selectedKey) this.updateTab(value);
		},
	},
	mounted() {
		if (!this.activeTab) {
			this.updateTab(this.tabs[0]?.key);
		} else {
			this.selectedKey = this.activeTab;
		}
	},
	methods: {
		selectTab(tabKey) {
			if (this.isChangeDisabled || this.selectedKey === tabKey) {
				return;
			}
			this.updateTab(tabKey);
		},
		updateTab(tabKey) {
			this.selectedKey = tabKey;
			this.$emit('tab-change', tabKey);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

// Main block
.tab {
	position: relative;
	height: 100%;
}

// Elements
.tab__overlay {
	position: absolute;
	top: 0;
	left: 0;
	z-index: 1000000;
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	opacity: 0.25;

	&:last-child {
		flex: 1;
	}
}

// .tab__wrapper {}

.tab__header {
	position: relative;
	z-index: 1; // avoid the tab's content going over the tab header
	display: flex;
	flex-direction: column;
	border: 0;
	border-bottom: 1px solid var(--color-grey-900);

	&.is-tagging-tool {
		border-bottom: none;
		margin-left: 0;
		margin-right: 0;
	}

	&.-sticky {
		position: sticky;
		background: var(--color-body-background);
	}
}

.tab__menu {
	display: flex;
	list-style-type: none;
	padding: 0;

	.tab.-simple & {
		gap: $sp4-5;
	}
}

.tab__menu-item {
	box-sizing: border-box;
	position: relative;
	display: flex;
	align-items: center;
	margin-bottom: -1px; // to overlap the borders
	padding-left: $sp4;
	padding-right: $sp4;
	padding-bottom: $sp4;
	cursor: pointer;
	color: var(--color-grey-500);
	gap: $sp3-5;
	border-bottom: 1px solid transparent;

	.tab:not(.-simple) & {
		border-bottom: 1px solid var(--color-grey-800);
		padding: 18px 8px 18px 8px;
		color: var(--color-grey-300);
		background-color: var(--color-black);
	}

	&.is-tagging-tool {
		justify-content: center;
		flex: 1 1 0;
	}

	&.is-active {
		color: var(--color-white);
		border-bottom-style: solid;
		border-bottom-width: 1px;
		border-bottom-color: var(--color-cta, var(--color-hype-yellow));

		.tab:not(.-simple) & {
			background-color: var(--one-panel-background-color);
			color: var(--color-white);

			body.-t-light & { // stylelint-disable-line
				color: var(--color-black);
			}
		}

		.tab.-simple & {
			color: var(--color-cta, var(--color-hype-yellow));
		}
	}

	.is-disabled & {
		cursor: default;
	}

	.is-change-disabled & {
		pointer-events: none;
	}
}

// .tab__menu-icon {}

.tab__menu-label {
	color: inherit;
}

// .tab__content {}
</style>
